import React, { InputHTMLAttributes, ReactNode, useState } from 'react';
import classNames from 'classnames';

import Icon, { Size as IconSize } from 'components/general/icon/icon';

const styles = require('./InputField.module.scss');

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'>;

interface InputFieldProps extends NativeInputProps {
  label?: string;
  hint?: string;
  error?: string;
  containerClassName?: string;
  inputClassName?: string;
  showVisibilityToggle?: boolean;
  focusHelper?: ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  hint,
  error,
  disabled,
  id,
  type = 'text',
  containerClassName,
  inputClassName,
  showVisibilityToggle,
  focusHelper,
  inputRef,
  onFocus,
  onBlur,
  ...inputProps
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputId = id || 'input-field';

  const shouldToggleVisibility = (type === 'password' || showVisibilityToggle) && !disabled;
  const resolvedType = shouldToggleVisibility ? (isVisible ? 'text' : 'password') : type;

  return (
    <div className={classNames(styles.wrapper, containerClassName)}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {inputProps.required && <abbr title='required'> *</abbr>}
        </label>
      )}

      <div
        className={classNames(
          styles.inputContainer,
          {
            [styles.focus]: isFocused,
            [styles.disabled]: disabled,
            [styles.error]: Boolean(error),
            [styles.withToggle]: shouldToggleVisibility,
          },
          inputClassName
        )}
      >
        <input
          id={inputId}
          type={resolvedType}
          className={styles.input}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          ref={inputRef}
          onFocus={event => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          onBlur={event => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          {...inputProps}
        />

        {shouldToggleVisibility && (
          <button
            type='button'
            className={styles.visibilityToggle}
            aria-label={isVisible ? 'Hide value' : 'Show value'}
            aria-pressed={isVisible}
            onClick={() => setIsVisible(value => !value)}
          >
            <Icon
              name={isVisible ? 'eyeClosed' : 'eye'}
              color={'black'}
              size={IconSize.Medium}
            />
          </button>
        )}
      </div>

      {focusHelper && isFocused && (
        <div className={styles.focusHelperPanel} aria-live='polite'>
          {focusHelper}
        </div>
      )}

      {(hint || error) && (
        <div
          className={classNames(styles.helper, { [styles.helperError]: Boolean(error) })}
          aria-live={error ? 'assertive' : 'polite'}
        >
          <Icon
            name={error ? 'warning' : 'moreInfo'}
            color={error ? 'flamingoMidDark' : 'silverSandDark'}
            size={IconSize.Small}
          />
          {error || hint}
        </div>
      )}
    </div>
  );
};

export default InputField;
